import { FC, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '../../components/Alert/Alert';
import BackButton from '../../components/BackButton';
import { ImageItem, LoadingImages } from '../../components/LoadingImages/LoadingImages';
import { StylesheetContext } from '../../contexts/StylesheetContext';
import { TicketContext } from '../../contexts/TicketContext';
import DateTime from '../../helpers/DateTime';
import { getMinimumStartDateForTicketFromNow } from '../../helpers/TicketHelpers';
import { useFormatting } from '../../services/hooks/useFormatting';
import usePageTitle from '../../services/hooks/usePageTitle';
import styles from './Appointment.module.scss';
import AppointmentWithoutTopSlots from './AppointmentWithoutTopSlots';
import AppointmentWithTopSlots from './AppointmentWithTopSlots';
import { useTopSlots } from './useTopSlots';

interface AppointmentProps {
  title?: string;
}

const Appointment: FC<AppointmentProps> = ({ title }) => {
  const { t } = useTranslation();

  usePageTitle(t('pageTitle:appointment'));

  const { ticket } = useContext(TicketContext);
  const { stylesheet } = useContext(StylesheetContext);
  const [isSlotBooking, setIsSlotBooking] = useState(false);
  const loadingImages = useMemo<ImageItem[]>(() => [
    {
      image: 'partners',
      text: t('appointmentLoading:selectingBestExpert'),
      ariaLabel: t('appointmentLoading:selectingBestExpert'),
    },
    {
      image: 'routes',
      text: t('appointmentLoading:calculatingEcoRoute'),
      ariaLabel: t('appointmentLoading:calculatingEcoRoute'),
    },
  ], [t]);

  const { formattedText: leadText } = useFormatting(
    stylesheet.appointmentSection.leadTextBlock.value,
    { replaceDate: ticket?.workOrder.appointment.firstPossiblePlanDate }
  );

  const { formattedText: reasonWeDontShowEarlierSlots } = useFormatting(
    stylesheet.appointmentSection.reasonWeDontShowEarlierSlots,
    { replaceDate: ticket?.workOrder.appointment.earliestSchedulingDate }
  );

  const { formattedText: lowAvailabilityText } = useFormatting(stylesheet.lowAvailabilityMessage?.value);
  const lowAvailabilityAlert = lowAvailabilityText && (
    <Alert level="warn" text={lowAvailabilityText} white />
  );

  const { minDate, maxDate } = useMemo(() => ({
    // TODO: remove both conditions ` || Date.now()` when we always have a ticket and stylesheet
    minDate: getMinimumStartDateForTicketFromNow(
      new Date(ticket?.workOrder.appointment.firstPossiblePlanDate || Date.now()),
      ticket?.workOrder.appointment.allowSameDayScheduling || false
    ),
    maxDate: new Date(ticket?.workOrder.appointment.latestPermittedPlanDate || Date.now()),
  }), [ticket]);

  const planDate = ticket?.workOrder.appointment.earliestSchedulingDate;
  const isPlanDateInFuture = planDate
    ? new Date(planDate) > DateTime.getDateXDaysInTheFuture(5)
    : false;

  const {
    areTopSlotsLoading,
    areAnySlotsLoading,
    topSlots,
    noTopSlots,
    highestSlot,
    availableDateEnd,
    fetchMoreTopSlots,
    refetchNoTopSlots,
  } = useTopSlots(minDate, maxDate, ticket);

  const canRetrieveMore = availableDateEnd < maxDate;

  if (!ticket) {
    // Should never happen, ErrorElement will be rendered instead
    return null;
  }

  const pageTitle = title ? title : t('appointment:title');
  const hasTopSlots = !!Object.keys(topSlots).length;

  if (areAnySlotsLoading) {
    return (
      <div>
        <h1>{pageTitle}</h1>
        <LoadingImages items={loadingImages} />
      </div>
    );
  }

  return (
    <div>
      {hasTopSlots ? (
        <>
          {!isSlotBooking && (
            <>
              <div className={styles.content}>
                <BackButton text={t('appointment:goBack')} />
              </div>
              <h1>{pageTitle}</h1>
              <p dangerouslySetInnerHTML={{ __html: leadText }} />
              {lowAvailabilityAlert}
            </>
          )}
          {!!reasonWeDontShowEarlierSlots && !!isPlanDateInFuture && (
            <Alert level="info" text={reasonWeDontShowEarlierSlots} white />
          )}
          <AppointmentWithTopSlots
            areLoadingSlots={areTopSlotsLoading}
            canRetrieveMore={canRetrieveMore}
            highestSlot={highestSlot}
            maxDate={availableDateEnd}
            minDate={minDate}
            onLoadMore={fetchMoreTopSlots}
            onSlotBooking={setIsSlotBooking}
            stylesheet={stylesheet}
            ticket={ticket}
            topSlots={topSlots}
          />
        </>
      ) : (
        <AppointmentWithoutTopSlots
          lowAvailabilityAlert={lowAvailabilityAlert}
          noTopSLots={noTopSlots}
          refetchNoTopSlots={refetchNoTopSlots}
          stylesheet={stylesheet}
          ticket={ticket}
        />
      )}
    </div>
  );
};

export default Appointment;
